import React, { useState, useEffect, useCallback } from 'react';
import { getAlerts, getAlert, updateAlertStatus, dispatchTeam, getTeams } from '../services/api';
import { joinAlert, leaveAlert } from '../services/socket';
import AlertList from '../components/dashboard/AlertList';
import AlertDetail from '../components/dashboard/AlertDetail';
import MapView from '../components/dashboard/MapView';
import { AlertTriangle, CheckCircle, Truck, Eye } from 'lucide-react';

export default function Dashboard({ alerts, setAlerts, liveLocations = {}, onAcknowledge }) {
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [alertDetail, setAlertDetail] = useState(null);
  const [teams, setTeams] = useState([]);
  const [filter, setFilter] = useState('active'); // active, all, resolved
  const [loading, setLoading] = useState(true);

  // Load alerts on mount
  useEffect(() => {
    async function load() {
      try {
        const [alertsRes, teamsRes] = await Promise.all([getAlerts(), getTeams()]);
        setAlerts(alertsRes.data.alerts || []);
        setTeams(teamsRes.data.teams || []);
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [setAlerts]);

  // Select alert and load detail
  const handleSelectAlert = useCallback(async (alert) => {
    if (selectedAlert) leaveAlert(selectedAlert.id);
    setSelectedAlert(alert);
    joinAlert(alert.id);
    try {
      const { data } = await getAlert(alert.id);
      setAlertDetail(data);
    } catch (err) {
      console.error('Failed to load alert detail:', err);
    }
  }, [selectedAlert]);

  // Update status
  const handleStatusUpdate = useCallback(async (alertId, status, notes) => {
    try {
      await updateAlertStatus(alertId, status, notes);
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, status } : a));
      if (alertDetail?.alert?.id === alertId) {
        setAlertDetail(prev => ({ ...prev, alert: { ...prev.alert, status } }));
      }
      // Stop the siren as soon as anything is acknowledged
      if (status === 'acknowledged' && onAcknowledge) onAcknowledge();
    } catch (err) {
      console.error('Status update failed:', err);
    }
  }, [alertDetail, setAlerts, onAcknowledge]);

  // Dispatch
  const handleDispatch = useCallback(async (alertId, teamId, personnelIds, eta) => {
    try {
      await dispatchTeam(alertId, { team_id: teamId, personnel_ids: personnelIds, eta_minutes: eta });
      handleStatusUpdate(alertId, 'dispatched');
    } catch (err) {
      console.error('Dispatch failed:', err);
    }
  }, [handleStatusUpdate]);

  // Filter alerts
  const filteredAlerts = alerts.filter(a => {
    if (filter === 'active') return !['resolved', 'cancelled'].includes(a.status);
    if (filter === 'resolved') return a.status === 'resolved';
    return true;
  });

  const stats = {
    pending: alerts.filter(a => a.status === 'pending').length,
    active: alerts.filter(a => ['acknowledged', 'dispatched', 'en_route', 'on_scene'].includes(a.status)).length,
    dispatched: alerts.filter(a => a.status === 'dispatched').length,
    resolved: alerts.filter(a => a.status === 'resolved').length,
  };

  return (
    <>
      {/* Stats Row */}
      <div className="stats-row">
        <div className="stat-card" onClick={() => setFilter('active')} style={{ cursor: 'pointer', borderColor: filter === 'active' ? 'var(--accent-red)' : 'var(--border)' }}>
          <div className="stat-value" style={{ color: 'var(--accent-red)' }}>{stats.pending}</div>
          <div className="stat-label">Pending</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--accent-amber)' }}>{stats.active}</div>
          <div className="stat-label">In Progress</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--accent-purple)' }}>{stats.dispatched}</div>
          <div className="stat-label">Dispatched</div>
        </div>
        <div className="stat-card" onClick={() => setFilter('resolved')} style={{ cursor: 'pointer', borderColor: filter === 'resolved' ? 'var(--accent-emerald)' : 'var(--border)' }}>
          <div className="stat-value" style={{ color: 'var(--accent-emerald)' }}>{stats.resolved}</div>
          <div className="stat-label">Resolved</div>
        </div>
      </div>

      {/* Main Grid: Alerts | Map | Detail */}
      <div className="dashboard-grid">
        <div className="dashboard-alerts">
          <AlertList
            alerts={filteredAlerts}
            selectedId={selectedAlert?.id}
            onSelect={handleSelectAlert}
            loading={loading}
          />
        </div>

        <div className="dashboard-map">
          <MapView
            alerts={filteredAlerts}
            selectedAlert={selectedAlert}
            onSelectAlert={handleSelectAlert}
            liveLocations={liveLocations}
          />
        </div>

        <div className="dashboard-detail">
          {selectedAlert && alertDetail ? (
            <AlertDetail
              alert={alertDetail.alert}
              timeline={alertDetail.timeline}
              dispatches={alertDetail.dispatches}
              locations={alertDetail.locations}
              teams={teams}
              onStatusUpdate={handleStatusUpdate}
              onDispatch={handleDispatch}
            />
          ) : (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              height: '100%', color: 'var(--text-muted)', gap: 12, padding: 40, textAlign: 'center'
            }}>
              <Eye size={40} strokeWidth={1} />
              <div style={{ fontSize: 14, fontWeight: 600 }}>Select an alert</div>
              <div style={{ fontSize: 12 }}>Click an alert from the list or map to view details, manage dispatch, and communicate.</div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
